/**
 * Header vehicle chip, its panel and the mobile vehicle bar (RAZ-337).
 *
 * One component drives all three surfaces, because they are three renderings of the
 * same two questions: "which vehicle is saved?" and "is the panel open?". The header
 * instance owns the panel; the mobile menu drawer mounts a second instance with
 * `enablePanel: false`, which shows the same two lines and asks the header to open.
 *
 * It composes the cascade rather than repeating it: the panel's inner scope is
 * `vehicleSelector`, which owns every option list and every "what is left to ask"
 * answer (src/js/components/vehicleSelector.js → src/js/utils/vehicleResolver.js).
 * Nothing here filters a catalogue, and nothing here builds a destination URL.
 *
 * The chip itself never loads the catalogue. It reads $store.vehicle, which Alpine's
 * $persist plugin hydrates from localStorage during init, so the saved vehicle is on
 * screen in the first painted frame. The 449-fitment payload is only fetched when the
 * panel is first opened, because that is the first moment anyone needs it.
 *
 * Design: Figma "2026 DC / Vehicle Chip" set 2078:31538 (state=empty / set / open) and
 * "Mobile Vehicle Bar" set 2394:33906.
 */

import { fill, labels as vehicleLabels } from '../utils/vehicleResolver.js';

/**
 * Any surface can open, close or toggle the header panel by firing these, and the panel
 * itself fires show and hide again as it opens and closes — by the chip, by the drawer,
 * by Escape, by the close button or by a click outside. They are the only signal the PDP
 * fitment card has for the aria-expanded on its "Change" / "Add your vehicle" button
 * (src/js/components/fitmentCard.js), so every state change announces itself.
 *
 * Re-entry is safe: open() and close() write isOpen before announcing, and openFromEvent()
 * returns early once isOpen is set.
 *
 * A show or toggle fired by a panel carries that panel's `instanceId`, so with more than
 * one chip on the page each instance can tell "I am being asked to open" from "somebody
 * else just opened, stand down". A show or toggle fired from anywhere else carries no
 * instanceId, and only the instance configured with `enableRemoteOpen` answers it.
 */
export const PANEL_SHOW_EVENT = 'arc:action:vehicle-panel-show';
export const PANEL_HIDE_EVENT = 'arc:action:vehicle-panel-hide';
export const PANEL_TOGGLE_EVENT = 'arc:action:vehicle-panel-toggle';

/** Fired once the shopper saves, so analytics and the PDP card can react in place. */
export const PANEL_SAVED_EVENT = 'arc:action:vehiclePanelSaved';

/** The other header layers, closed as the panel opens so two never overlap. */
const RIVAL_HIDE_EVENTS = ['arc:action:header-search-hide', 'arc:action:mobile-menu-hide'];

/**
 * The theme's own "a header layer is open" signal. The Shop megamenu opens on hover and
 * answers to no hide event, but it does listen for this: it closes the moment a layer
 * reports itself visible and stays suppressed until that layer reports itself hidden. The
 * account dropdown and the block-builder drawers already use it, so the panel joins them
 * rather than reaching into the megamenu's internals.
 */
const HEADER_LAYER_EVENT = 'block-builder-state-change';
const HEADER_LAYER_ID = 'rb-vehicle-panel';

/** The mobile drawer's vehicle row, in sections/razorback-mobile-menu.liquid. */
const DRAWER_ROW_SELECTOR = '.rb-vehicle-chip-row';

/** The class razorback-mobile-menu.liquid already uses to lock body scroll. */
const SCROLL_LOCK_CLASS = 'rb-mob-lock';

/**
 * At and below this width the panel is a bottom sheet rather than a 300px dropdown: the
 * theme's Tablet Breakpoint, where the header switches to its mobile masthead. Kept in step
 * with the media queries in rb-vehicle-chip.scss.
 */
const DEFAULT_SHEET_BREAKPOINT = 1024;

/** Merchant-overridable copy; the section passes its own settings over the top. */
const DEFAULT_COPY = {
  empty: 'Find your vehicle',
  change: 'Change',
  allVariants: 'All variants',
  nextStep: 'Add your {next_step}',
  saved: 'Vehicle saved. Taking you to the covers that fit.',
  savedStay: 'Vehicle saved \u00b7 now checking fit for your {model}',
  savedElsewhere: 'Vehicle saved \u00b7 {make} {model}',
  removed: 'Vehicle removed. Choose another to see covers that fit.',
  lost: 'The vehicle you saved is no longer in our range. Choose another to see covers that fit.',
};

/** Fired by the store when it drops a vehicle; the reason says who dropped it. */
const VEHICLE_CLEARED_EVENT = 'arc:action:vehicleCleared';

/**
 * The `reason` src/js/stores/vehicle.js gives when it drops a vehicle the catalogue no
 * longer holds, as opposed to one the shopper removed or one that timed out. Declared
 * here rather than imported, because importing the store would pull Alpine into this
 * module — the same reason the panel event names above are declared rather than shared.
 */
const CLEARED_BY_CATALOGUE = 'catalogue';


/**
 * Page types that re-evaluate the saved vehicle where the shopper already is: the PDP
 * fitment card, the collection vehicle bar and the cart's mismatch note all re-render
 * around the store's own `arc:action:vehicleSet:success`.
 *
 * This list no longer decides whether a save navigates — saving stays put everywhere
 * unless the section turns redirect-on-save back on (see `shouldRedirectOnSave`). All
 * it decides now is which confirmation the shopper reads: on these pages something
 * visibly changed beneath the panel, so the confirmation may point at it; anywhere
 * else nothing on screen changed but the chip, so the confirmation only names what
 * was saved rather than promising covers that are not on this page.
 */
const VEHICLE_AWARE_PAGE_TYPES = ['product', 'collection', 'cart'];

/** How long the save-and-stay confirmation stays on screen, per the solution doc. */
const CONFIRM_DURATION = 4000;

/**
 * Debug-only warning, so a broken host never takes the header down with it.
 * @param {string} message
 * @param {*} detail
 */
function warnOnce(message, detail) {
  if (typeof window !== 'undefined' && window.enableDebugMode) { console.warn(message, detail); }
}

/**
 * Fire a window event without letting a listener's error reach the caller.
 * @param {string} name
 * @param {Object} [detail]
 */
function announce(name, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch (error) {
    warnOnce(`vehicleChip: ${name} could not be dispatched.`, error);
  }
}

/**
 * Join the parts of a chip line, dropping the ones this vehicle does not have.
 * @param {Array<string>} parts
 * @param {string} separator
 * @returns {string}
 */
function joinParts(parts, separator) {
  return parts.filter((part) => typeof part === 'string' && part !== '').join(separator);
}

export default {
  name: 'vehicleChip',

  /**
   * @param {Object} [params]
   * @param {boolean} [params.enablePanel] does this instance own the panel? (default true)
   * @param {string} [params.instanceId] this instance's name on the page's event bus, so two
   *   chips can tell their own show events from each other's. The panel element's id is what
   *   Liquid passes, because that is already unique per instance.
   * @param {boolean} [params.enableRemoteOpen] answer show/toggle events fired from elsewhere
   *   on the page — the mobile drawer's row, the PDP fitment card's button (default true).
   *   Exactly one instance on a page should have this on.
   * @param {number} [params.sheetBreakpoint] px at and below which the panel is a sheet (default 1024)
   * @param {string} [params.hubUrl] fallback destination when a save resolves to nothing
   * @param {string} [params.pageType] Liquid's `request.page_type`, which decides which
   *   save confirmation the shopper reads (see VEHICLE_AWARE_PAGE_TYPES)
   * @param {boolean} [params.enableRedirectOnSave] restore the old behaviour where saving
   *   leaves the page for the covers that fit (default false: saving stays put)
   * @param {Object} [params.copy] see DEFAULT_COPY
   * @returns {Object}
   */
  component(params = {}) {
    const config = params && typeof params === 'object' ? params : {};
    const copy = { ...DEFAULT_COPY, ...(config.copy ?? {}) };
    const hasPanel = config.enablePanel !== false;
    const answersRemote = config.enableRemoteOpen !== false;
    const instanceId = typeof config.instanceId === 'string' && config.instanceId !== ''
      ? config.instanceId
      : null;
    const pageType = typeof config.pageType === 'string' ? config.pageType.trim().toLowerCase() : '';
    // Only the nav chip is the mobile vehicle bar, so only it steps aside for the drawer.
    const yieldsToDrawer = config.chipStyle === 'nav' && hasPanel;
    const breakpoint = Number(config.sheetBreakpoint) > 0
      ? Number(config.sheetBreakpoint)
      : DEFAULT_SHEET_BREAKPOINT;

    return {
      /* -------------------------------------------------------------- */
      /* State                                                          */
      /* -------------------------------------------------------------- */

      /** True while the panel is on screen. The chip's aria-expanded reads this. */
      isOpen: false,

      /** True once the panel has been opened at least once, which mounts the cascade. */
      isMounted: false,

      /** True when the viewport wants the sheet layout rather than the dropdown. */
      isSheet: false,

      /**
       * True while the mobile drawer's vehicle row is on screen. The menu design shows the
       * vehicle once, in the drawer, so the nav chip's bar steps aside (see
       * rb-vehicle-chip.scss). Only a nav chip that owns the panel ever sets it.
       */
      isYieldingToDrawer: false,

      /** Watches the drawer rows for watchDrawerRows(), kept for destroy(). */
      drawerRowObserver: null,

      /** What the chip's live region says after a save or a removal. */
      message: '',

      /** Merchant copy, readable from the template. */
      copy,

      /** Does this instance render a panel, or only ask the header's one to open? */
      hasPanel,

      /** This instance's name on the page's event bus, or null when it was never given one. */
      instanceId,

      /** Does this instance answer show/toggle events fired from elsewhere on the page? */
      answersRemote,

      /** The media query that decides dropdown vs sheet, kept so it can be unbound. */
      sheetQuery: null,

      /** Bound listener, kept for destroy(). */
      onSheetChange: null,

      /**
       * True while the click that opened the panel is still travelling towards the
       * document, where @click.outside listens. Without it, a click on the mobile
       * drawer's vehicle row opens the panel and then immediately closes it again:
       * the row is outside this wrapper, so the one click does both.
       */
      isIgnoringOutside: false,

      /** Timer that clears isIgnoringOutside, kept so destroy() can cancel it. */
      outsideTimer: null,

      /**
       * The control that opened the panel, so closing hands focus back to it rather
       * than to the header chip. The PDP fitment card's "Change" button sits in the buy
       * rail; dropping a keyboard shopper back at the masthead costs them the logo, the
       * search field, account, cart and the whole nav before they reach Add to cart again.
       */
      opener: null,

      /** True while the save-and-stay confirmation is on screen. */
      isConfirming: false,

      /** Timer that hides the confirmation, kept so destroy() can cancel it. */
      confirmTimer: null,

      /** Bound listener for the store's clear, kept for destroy(). */
      onVehicleCleared: null,

      /* -------------------------------------------------------------- */
      /* Lifecycle                                                      */
      /* -------------------------------------------------------------- */

      init() {
        this.watchViewport();
        this.watchLostVehicle();
        this.watchDrawerRows();
      },

      destroy() {
        this.releaseScroll();
        if (this.drawerRowObserver) {
          this.drawerRowObserver.disconnect();
          this.drawerRowObserver = null;
        }
        if (this.outsideTimer) clearTimeout(this.outsideTimer);
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        if (this.onVehicleCleared) {
          window.removeEventListener(VEHICLE_CLEARED_EVENT, this.onVehicleCleared);
          this.onVehicleCleared = null;
        }
        if (!this.sheetQuery || !this.onSheetChange) return;
        if (typeof this.sheetQuery.removeEventListener === 'function') {
          this.sheetQuery.removeEventListener('change', this.onSheetChange);
        }
      },

      /**
       * Say something when the catalogue loses the shopper's vehicle.
       *
       * The store drops a vehicle the catalogue no longer holds, because there is no
       * honest way to keep naming a generation or a variant that has been deleted — the
       * chip would be claiming knowledge of a truck the site cannot recognise, and would
       * be contradicting the finder, the collection bar and the PDP card, all of which
       * read the catalogue. So the chip goes back to "Find your vehicle".
       *
       * Going back silently would be the wrong half of that decision: a shopper who set
       * their vehicle weeks ago and finds the header empty has no idea why, and no reason
       * to think setting it again would stick. One polite line in the live region says
       * what happened and what to do, and it is said once — the vehicle has left
       * localStorage, so no later page load repeats it.
       *
       * Only the instance that owns the panel speaks. The mobile drawer renders a second
       * chip with its own live region, and the same sentence read out twice is noise.
       */
      watchLostVehicle() {
        if (!hasPanel) return;
        this.onVehicleCleared = (event) => {
          if (event?.detail?.reason !== CLEARED_BY_CATALOGUE) return;
          this.message = copy.lost;
        };
        try {
          window.addEventListener(VEHICLE_CLEARED_EVENT, this.onVehicleCleared);
        } catch (error) {
          this.onVehicleCleared = null;
          warnOnce('vehicleChip: the store could not be watched for a lost vehicle.', error);
        }
      },

      /**
       * Step the nav bar aside while a drawer vehicle row is on screen.
       *
       * The drawer answers only a bare toggle event and can also close on Escape or an
       * outside tap without announcing anything, so counting events would drift. What
       * cannot drift is the row itself: the drawer is display: none when shut, so the row
       * has a box exactly while the drawer is open, and a ResizeObserver sees it gain and
       * lose that box whichever way the drawer moved.
       */
      watchDrawerRows() {
        if (!yieldsToDrawer || typeof ResizeObserver !== 'function') return;
        try {
          const rows = [...document.querySelectorAll(DRAWER_ROW_SELECTOR)];
          if (rows.length === 0) return;
          const check = () => {
            this.isYieldingToDrawer = rows.some((row) => row.getClientRects().length > 0);
          };
          // A frame later, not inside the callback: collapsing the bar resizes the header in
          // the same frame header.js is observing it, which the browser reports as a
          // "ResizeObserver loop" error on every drawer open and close.
          const schedule = () => {
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(check);
            else check();
          };
          this.drawerRowObserver = new ResizeObserver(schedule);
          rows.forEach((row) => this.drawerRowObserver.observe(row));
          check();
        } catch (error) {
          warnOnce('vehicleChip: the drawer row could not be watched.', error);
        }
      },

      /**
       * Track the sheet breakpoint in JavaScript as well as CSS, because the scroll
       * lock and the chevron direction cannot be expressed in a media query.
       */
      watchViewport() {
        try {
          this.sheetQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
          this.isSheet = this.sheetQuery.matches;
          this.onSheetChange = (event) => {
            this.isSheet = event.matches;
            // A rotation that turns the sheet back into a dropdown must not leave the
            // page scroll locked behind a panel that is no longer covering it.
            if (this.isOpen && !this.isSheet) this.releaseScroll();
            else if (this.isOpen && this.isSheet) this.lockScroll();
          };
          if (typeof this.sheetQuery.addEventListener === 'function') {
            this.sheetQuery.addEventListener('change', this.onSheetChange);
          }
        } catch (error) {
          // No matchMedia (a very old browser, or a test): the dropdown is the safe default.
          warnOnce('vehicleChip: the viewport could not be watched.', error);
        }
      },

      /* -------------------------------------------------------------- */
      /* Reading the saved vehicle                                      */
      /* -------------------------------------------------------------- */

      /** The saved vehicle, or null. Hydrated by $persist before the first paint. */
      get savedVehicle() {
        return this.$store?.vehicle?.vehicle ?? null;
      },

      get hasVehicle() {
        return this.savedVehicle !== null && this.savedVehicle !== undefined;
      },

      /** True when the catalogue no longer holds what this vehicle named. */
      get isStale() {
        return this.$store?.vehicle?.isStale === true;
      },

      /** The resolver's label map — the only source of the chip's two lines. */
      get vehicleLabels() {
        return vehicleLabels(this.savedVehicle);
      },

      /** Line one: "TOYOTA HILUX". Uppercased by CSS, never by the data. */
      get chipTitle() {
        if (!this.hasVehicle) return copy.empty;
        const map = this.vehicleLabels;
        return joinParts([map.make, map.model], ' ');
      },

      /**
       * Line two: "8TH GEN · SR5" for a resolved vehicle, "ADD YOUR VARIANT" while a
       * level is still unanswered, and the all-variants copy for the models whose
       * fitments carry neither a generation nor a variant (Kia EV5, Tasman).
       *
       * @returns {string}
       */
      get chipSubtitle() {
        if (!this.hasVehicle) return '';
        const saved = this.savedVehicle;
        if (saved.complete !== true) return fill(copy.nextStep, saved);
        const map = this.vehicleLabels;
        return joinParts([map.series, map.variant], ' · ') || copy.allVariants;
      },

      /** The chip reverts to its empty label while the panel is open, as the master draws. */
      get isShowingEmpty() {
        return !this.hasVehicle || this.isOpen;
      },

      /** Which chevron the chip carries: up while open, right on the mobile bar. */
      get chevronClass() {
        if (this.isOpen) return 'icon-chevron-up';
        return this.isSheet ? 'icon-chevron-right' : 'icon-chevron-down';
      },

      /**
       * Every chevron icon class, on or off. The markup ships `icon-chevron-down` so the
       * chip has its chevron before Alpine starts; a plain string binding would only ever
       * add to that class, leaving two glyphs fighting, so this switches the others off.
       */
      get chevronClasses() {
        const current = this.chevronClass;
        return Object.fromEntries(
          ['icon-chevron-up', 'icon-chevron-right', 'icon-chevron-down'].map((name) => [name, name === current])
        );
      },

      /** What the chip's accessible name says, so it is never just a chevron. */
      get triggerLabel() {
        if (!this.hasVehicle) return copy.empty;
        return `${copy.change}: ${joinParts([this.chipTitle, this.chipSubtitle], ' ')}`;
      },

      /* -------------------------------------------------------------- */
      /* Opening and closing                                            */
      /* -------------------------------------------------------------- */

      /** Open the panel, or ask the header's instance to when this one has none. */
      open() {
        if (!hasPanel) {
          announce(PANEL_SHOW_EVENT, { source: 'drawer' });
          return;
        }
        if (this.isOpen) return;

        RIVAL_HIDE_EVENTS.forEach((name) => announce(name));
        announce(HEADER_LAYER_EVENT, { state: 'visible', componentId: HEADER_LAYER_ID });
        this.rememberOpener();
        this.isMounted = true;
        this.isOpen = true;
        this.message = '';
        this.dismissConfirmation();
        this.holdOutside();
        if (this.isSheet) this.lockScroll();
        announce(PANEL_SHOW_EVENT, { source: 'panel', instanceId });
      },

      /**
       * Is this show/toggle event addressed to this instance?
       *
       * An event a panel fired names its own instance, so a second chip on the page reads
       * "not me" and stands down rather than opening a second panel. An event fired from
       * anywhere else — the mobile drawer's vehicle row, the PDP fitment card's Change
       * button — names nobody, and only the instance configured to answer for the page
       * takes it.
       *
       * @param {Event} [event]
       * @returns {boolean}
       */
      isAddressedTo(event) {
        // An instance with no panel has nothing to open and must never answer a remote
        // request with a remote request of its own.
        if (!hasPanel) return false;
        const from = event?.detail?.instanceId ?? null;
        if (from === null || from === undefined) return this.answersRemote;
        return instanceId !== null && from === instanceId;
      },

      /**
       * Open on a show event, or stand down when the event says another panel just opened.
       * @param {Event} [event]
       */
      openFromEvent(event) {
        if (this.isAddressedTo(event)) {
          this.open();
          return;
        }
        this.standDown();
      },

      /**
       * Toggle on a toggle event, or stand down when it belongs to another instance.
       * @param {Event} [event]
       */
      toggleFromEvent(event) {
        if (this.isAddressedTo(event)) {
          this.toggle();
          return;
        }
        this.standDown();
      },

      /**
       * Give up the screen to a panel that has just opened elsewhere on the page.
       *
       * Deliberately quiet: it announces no hide. The hide would be heard by the instance
       * that is opening — the two are listening to the same bus — and would close the panel
       * the shopper actually asked for. Focus is left alone for the same reason: it now
       * belongs to the control that opened the other panel.
       */
      standDown() {
        if (!hasPanel || !this.isOpen) return;
        this.isOpen = false;
        this.releaseScroll();
      },

      /**
       * Ignore outside clicks until the current event has finished bubbling. A
       * timeout rather than $nextTick, because a microtask can still run between two
       * listeners for the same click.
       */
      holdOutside() {
        this.isIgnoringOutside = true;
        if (this.outsideTimer) clearTimeout(this.outsideTimer);
        this.outsideTimer = setTimeout(() => { this.isIgnoringOutside = false; }, 0);
      },

      /**
       * Note whatever had focus as the panel opened. Every route in fires while the
       * opening control still holds focus — the chip's own click, the drawer row's, and
       * the PDP card's button, whose window event is dispatched from its click handler —
       * so `document.activeElement` is that control.
       */
      rememberOpener() {
        try {
          const active = document.activeElement;
          this.opener = active && active !== document.body ? active : null;
        } catch (error) {
          this.opener = null;
          warnOnce('vehicleChip: the opening control could not be noted.', error);
        }
      },

      /** Close on a click elsewhere on the page, unless that click is what opened it. */
      closeOnOutside() {
        if (this.isIgnoringOutside) return;
        this.close();
      },

      /**
       * Close the panel and put focus back on the control that opened it — the header
       * chip, the drawer's row, or the PDP fitment card's button. The chip is only the
       * fallback, for an opener that has since left the page: the mobile drawer closes
       * as the panel opens, taking its row with it.
       */
      /**
       * @param {Object} [options]
       * @param {boolean} [options.shouldRestoreFocus] false when the shopper has already put
       *   focus somewhere else, so handing it back to the chip would pull it away
       */
      close({ shouldRestoreFocus = true } = {}) {
        if (!hasPanel || !this.isOpen) return;
        this.isOpen = false;
        this.releaseScroll();
        announce(HEADER_LAYER_EVENT, { state: 'hidden', componentId: HEADER_LAYER_ID });
        announce(PANEL_HIDE_EVENT, { source: 'panel', instanceId });
        if (shouldRestoreFocus) this.$nextTick(() => this.restoreFocus());
        else this.opener = null;
      },

      /**
       * Close because another header layer is opening — the search panel, which opens when
       * the masthead search field takes focus. Only one layer is ever open at a time, and
       * focus stays in the field the shopper just chose.
       */
      giveWay() {
        this.close({ shouldRestoreFocus: false });
      },

      toggle() {
        if (this.isOpen) this.close();
        else this.open();
      },

      /**
       * Alias for open(), for a host whose own scope already has an `open` of its own —
       * the mobile menu drawer keeps its drawer state in `open`, and a reader should not
       * have to work out which one a template means.
       */
      openPanel() {
        this.open();
      },

      /**
       * Hand focus back to the opener, or to this instance's own trigger when the opener
       * can no longer take it.
       */
      restoreFocus() {
        const opener = this.opener;
        this.opener = null;
        try {
          if (opener && opener !== this.$refs?.trigger && this.canTakeFocus(opener)) {
            opener.focus();
            return;
          }
        } catch (error) {
          warnOnce('vehicleChip: focus could not be restored to the opener.', error);
        }
        this.focusTrigger();
      },

      /**
       * Is this element still on the page and still painted? A control inside a drawer
       * that has since closed is both detached-looking and invisible, and focusing it
       * would either throw or silently strand the shopper.
       *
       * @param {Element} element
       * @returns {boolean}
       */
      canTakeFocus(element) {
        if (!element || typeof element.focus !== 'function') return false;
        if (element.isConnected === false) return false;
        if (typeof element.getClientRects !== 'function') return true;
        return element.getClientRects().length > 0;
      },

      /** Move focus to whichever trigger this instance renders. */
      focusTrigger() {
        try {
          const trigger = this.$refs?.trigger;
          if (trigger && document.activeElement !== trigger) trigger.focus();
        } catch (error) {
          warnOnce('vehicleChip: focus could not be restored to the chip.', error);
        }
      },

      lockScroll() {
        try {
          document.documentElement.classList.add(SCROLL_LOCK_CLASS);
        } catch (error) {
          warnOnce('vehicleChip: the page scroll could not be locked.', error);
        }
      },

      releaseScroll() {
        try {
          document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
        } catch (error) {
          warnOnce('vehicleChip: the page scroll could not be released.', error);
        }
      },

      /* -------------------------------------------------------------- */
      /* Saving and removing                                            */
      /* -------------------------------------------------------------- */

      /**
       * True when this page re-renders around the saved vehicle. The page type comes
       * from Liquid's `request.page_type`; an instance mounted without one (a test, or
       * a surface that never says) reads as "nothing here re-evaluates", which is the
       * honest default for the confirmation wording.
       */
      get isVehicleAwarePage() {
        return VEHICLE_AWARE_PAGE_TYPES.includes(pageType);
      },

      /**
       * Does a save leave the page? No, on every page type, unless the merchant turns
       * the section setting back on.
       *
       * Staying put is the product decision: a shopper who names their vehicle in the
       * header has asked the site to remember it, not to take them somewhere. The
       * capability is kept rather than deleted because the original design sent the
       * shopper straight to the covers that fit, and a merchant may still want that.
       */
      get shouldRedirectOnSave() {
        return config.enableRedirectOnSave === true;
      },

      /**
       * Save the vehicle the panel resolved.
       *
       * Saving sets the vehicle in place. The panel closes, PANEL_SAVED_EVENT and the
       * store's own `vehicleSet:success` make the fitment card, the vehicle bar and the
       * cart note re-evaluate around the new vehicle where the shopper already is, and
       * a brief confirmation says the save landed — because a panel that simply
       * vanishes is indistinguishable from one that failed.
       *
       * Only a merchant who has turned redirect-on-save back on gets the original
       * journey, where the save leaves for the covers that fit.
       *
       * Both arguments come from the inner `vehicleSelector` scope, which is what
       * built them: this component never resolves a destination itself.
       *
       * @param {Object|null} vehicle buildVehicle() output
       * @param {string|null} href destinationUrl() output
       */
      saveVehicle(vehicle, href) {
        if (!vehicle) return;

        const saved = this.$store?.vehicle?.set(vehicle) ?? null;
        if (!saved) return;

        announce(PANEL_SAVED_EVENT, {
          vehicle: saved,
          destinationHref: href ?? null,
          isNavigating: this.shouldRedirectOnSave,
        });

        if (!this.shouldRedirectOnSave) {
          this.close();
          this.confirmSave(saved);
          return;
        }

        this.message = copy.saved;
        this.close();

        const target = href || config.hubUrl || '';
        if (!target) return;
        try {
          window.location.assign(target);
        } catch (error) {
          warnOnce('vehicleChip: the destination could not be opened.', error);
        }
      },

      /**
       * Show the save-and-stay confirmation for four seconds.
       *
       * The wording follows the page: where the fitment card, the vehicle bar or the
       * cart note has just re-evaluated beneath the panel it may point at them, and
       * everywhere else it only names the vehicle that was saved.
       *
       * The text is the chip's
       * live-region message as well as the flyout's, so it is announced once and read
       * once; the flyout itself is inert, so it can never take focus and then vanish
       * from under a keyboard shopper mid-tab.
       *
       * @param {Object} saved the vehicle the store kept
       */
      confirmSave(saved) {
        const template = this.isVehicleAwarePage ? copy.savedStay : copy.savedElsewhere;
        this.message = fill(template, saved) || fill(copy.savedStay, saved) || copy.saved;
        this.isConfirming = true;
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        this.confirmTimer = setTimeout(() => { this.isConfirming = false; }, CONFIRM_DURATION);
      },

      /** Take the confirmation off screen early — opening the panel again, or removing. */
      dismissConfirmation() {
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        this.confirmTimer = null;
        this.isConfirming = false;
      },

      /**
       * Forget the saved vehicle. The panel stays open on an empty cascade, because a
       * shopper who removes a vehicle in the panel is usually about to pick another.
       * The caller resets the inner cascade, which is the scope that owns the fields.
       */
      removeVehicle() {
        this.$store?.vehicle?.clear('shopper');
        this.dismissConfirmation();
        this.message = copy.removed;
      },
    };
  },
};
